import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  AnswerSurveyDocument,
  MyTrainingsSurveysAnswersIndexDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { QuestionnaireResponse } from 'modules/questionnaires/components/QuestionnaireResponse'
import { AnswerType } from 'modules/questionnaires/lib/types'

import { AnswerMyTrainings, questionnaireAnswerFunctions } from './useQuestionnaireAnswer'

const Answer = () => {
  const { materialized_form_id, membership_id } = useParams<{
    materialized_form_id: string
    membership_id: string
  }>()
  const {
    data,
    loading: loadingAnswers,
    error,
  } = useQuery(MyTrainingsSurveysAnswersIndexDocument, {
    variables: {
      membershipId: membership_id,
      materializedFormId: materialized_form_id ?? '',
    },
  })
  const { company } = useCurrentSession()
  const { encodeQuestionnaireResponseContent, decodeAnswerType, extractComments } =
    questionnaireAnswerFunctions()
  const sections = data?.forms.materializedForm?.materializedSections ?? []
  const questions = data?.forms.materializedForm?.materializedQuestions ?? []
  const answers: AnswerMyTrainings[] =
    data?.forms.answersConnection.nodes.map((answer) => ({
      id: answer.id,
      answer: answer.answer ?? '',
      comment: answer.comment ?? undefined,
      materializedQuestion: answer.materializedQuestion ?? undefined,
      materializedQuestionOptions:
        answer.materializedQuestionOptionConnection.nodes.map((option) => ({
          id: option.id,
          value: option.value ?? '',
        })) ?? [],
    })) ?? []
  const encodedQuestionnaire = useMemo(() => {
    return encodeQuestionnaireResponseContent(sections, questions, answers)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.length, questions.length, answers.length])
  const [mergeContent, setMergeContent] = useState(encodedQuestionnaire)

  useEffect(() => {
    setMergeContent(encodedQuestionnaire)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encodedQuestionnaire.length])
  const uuids = useUnsafeMemo(() => {
    const sectionQuestionsUuids = sections
      .flatMap((section) => section.materializedQuestions)
      .map((question) => question.id)
    const questionsUuids = questions.map((question) => question.id)
    return [...questionsUuids, ...sectionQuestionsUuids]
  }, [questions, sections])

  const [content, setContent] = useState<AnswerType[]>(
    uuids.map((question) => {
      return { uuid: question }
    })
  )

  useEffect(() => {
    if (content.length === 0) {
      setContent(
        uuids.map((question) => {
          return { uuid: question }
        })
      )
    } else {
      setContent((prev) => [...prev])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content.length, uuids.length])

  const [bulkCreateFormsAnswer, { loading: loadingCreateAnswer }] = useMutation(
    AnswerSurveyDocument,
    {
      extractorFn: (responseData) => responseData.formsMutations.bulkCreateFormsAnswer,
      refetchQueries: [MyTrainingsSurveysAnswersIndexDocument],
      awaitRefetchQueries: true,
    }
  )

  const updateAnswer = (updatedAnswer: AnswerType) => {
    const index = content.findIndex((answer) => answer.uuid === updatedAnswer.uuid)
    if (index !== -1) {
      content[index] = { ...updatedAnswer }
      setContent(content)
    } else {
      content.push(updatedAnswer)
      setContent(content)
    }
  }

  const onSubmit = async () => {
    const result = await bulkCreateFormsAnswer({
      variables: {
        answers: content.map((answer) => ({
          materializedQuestionId: answer.uuid,
          answer: decodeAnswerType(answer) ? decodeAnswerType(answer)?.toString() : undefined,
          materializedQuestionOptionIds: 'answerChoice' in answer ? answer.answerChoice : [],
          comment: extractComments(answer),
        })),
        companyId: company.id.toString(),
        membershipId: membership_id ?? '',
      },
    })

    const encodedNewContent = encodeQuestionnaireResponseContent(
      sections,
      questions,
      content.map((answer) => ({
        answer: decodeAnswerType(answer) ? decodeAnswerType(answer)?.toString() : undefined,
        id: answer.uuid,
        comment: extractComments(answer),
        materializedQuestion: { id: answer.uuid },
        materializedQuestionOptions:
          'answerChoice' in answer
            ? answer.answerChoice.map((choice) => ({ id: choice, value: choice }))
            : [],
      }))
    )
    setMergeContent(encodedNewContent)

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('trainings.my_surveys.surveys_answer.send.error'),
      })
    })
    const formType = data?.forms.materializedForm?.form?.formType
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message:
          formType === 'satisfaction'
            ? i18n.t('trainings.my_surveys.surveys_answer.send.satisfaction_success')
            : i18n.t('trainings.my_surveys.surveys_answer.send.effectiveness_success'),
      })
    }
  }
  return (
    <ResponsivePage.NestedBody loading={loadingCreateAnswer || loadingAnswers} error={error}>
      <Box height="full">
        <Box height="full" paddingX="s24">
          <Box height="full">
            <QuestionnaireResponse
              readonly={answers?.length > 0}
              content={mergeContent}
              emptyState={sections.length === 0 && questions.length === 0 ? <></> : undefined}
              onAnswer={(answer) => updateAnswer(answer)}
            />
          </Box>
        </Box>
        <Box
          flexDirection="row"
          alignSelf="end"
          justifyContent="flexEnd"
          position="sticky"
          maxHeight="fit-content"
          style={{
            bottom: '0',
            marginTop: '16px',
          }}
          width="full"
          background="white"
          paddingY="s16"
          paddingX="s40"
          border={{
            top: {
              width: 's2',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Button onClick={onSubmit} type="primary" submit disabled={answers.length > 0}>
            {i18n.t('trainings.my_surveys.surveys_answer.send.button')}
          </Button>
        </Box>
      </Box>
    </ResponsivePage.NestedBody>
  )
}

export default Answer
